'use client';

import { Box, freddyTheme, Typography } from '@coolblue-development/freddy';
import { useStreamHandler } from '@coolblue-development/next-chat/client';
import type { PostMessageResponse, UiMessage } from '@coolblue-development/next-chat/types';
import {
  AdviceCue,
  type AdviceCueState,
  FeedbackLink
} from '@coolblue-development/next-chatbot-ui/client';
import type { Product } from '@features/product/typings';
import fetchAdviceAgentSession from '@features/product-page/services/fetchAdviceAgentSession';
import { useBreakpoints } from '@hooks/useBreakpoints';
import useClientStorage from '@hooks/useClientStorage';
import { useIsHydrated } from '@hooks/useIsHydrated';
import { useTranslation } from '@i18n/useTranslation';
import { type ReactNode, useEffect, useRef, useState, useTransition } from 'react';

type Props = {
  accountName?: string;
  chatSource: string;
  correlationId: string;
  endpointUrl: string;
  isShownInternallyOnly: boolean;
  product: Product;
};

const APPLE_BRAND_ID = '32';
const PRODUCT_TYPE_IDS = {
  LAPTOP: '17632',
  TABLET: '2101',
  MOBILE_PHONE: '2093'
};

const AdviceCueWrapper = ({
  accountName,
  chatSource,
  correlationId,
  endpointUrl,
  isShownInternallyOnly,
  product
}: Props): ReactNode => {
  const { t } = useTranslation('webshop.productPage');
  const { isMobile } = useBreakpoints();

  const boxRef = useRef<HTMLDivElement>(null);
  const [, startTransition] = useTransition();

  const isHydrated = useIsHydrated();
  const [history, setHistory] = useState<UiMessage[]>([]);
  const [initialConversationId, setInitialConversationId] = useState<string | undefined>(undefined);
  const [followUpQuestions, setFollowUpQuestions] = useClientStorage<string[] | []>(
    'adviceCueFollowUpQuestions_' + product.id,
    [],
    'localStorage'
  );

  const { brand, productType, id } = product;
  const { productTypeId } = productType;

  let productTypeName = productType.singularName;

  if (productTypeId && String(brand?.id) === APPLE_BRAND_ID) {
    if (productTypeId === PRODUCT_TYPE_IDS.LAPTOP) {
      productTypeName = 'MacBook';
    } else if (productTypeId === PRODUCT_TYPE_IDS.TABLET) {
      productTypeName = 'iPad';
    } else if (productTypeId === PRODUCT_TYPE_IDS.MOBILE_PHONE) {
      productTypeName = 'iPhone';
    }
  }

  useEffect(() => {
    startTransition(() => {
      void (async () => {
        const { historyMessages, id: conversationIdFromHistory } = await fetchAdviceAgentSession(
          chatSource,
          id.toString()
        );
        setHistory(historyMessages);
        setInitialConversationId(conversationIdFromHistory);
      })();
    });
  }, [chatSource, id]);

  const {
    state,
    sendUserMessage,
    resendFailedUserMessage,
    startConversation,
    conversationId: streamConversationId
  } = useStreamHandler<AdviceCueState>({
    conversationEndpointUrl: endpointUrl,
    initialConversationId,
    initialState: {
      uiMessages: [],
      showError: false,
      followUpQuestions: product.adviceCue.questions
    },
    correlationId,
    customMessageHandlers: {
      AdviceCueFollowupQuestions: (
        incomingMessage: PostMessageResponse,
        currentState: AdviceCueState
      ) => {
        if (incomingMessage.type === 'AdviceCueFollowupQuestions') {
          if (incomingMessage?.payload?.questions?.length)
            setFollowUpQuestions(incomingMessage?.payload?.questions);
          return {
            ...currentState,
            followUpQuestions: incomingMessage?.payload?.questions || []
          };
        }
        return currentState;
      },
      agentProgress: (incomingMessage: PostMessageResponse, currentState: AdviceCueState) => {
        if (incomingMessage.type === 'agentProgress') {
          return {
            ...currentState,
            progressMessage: incomingMessage.payload?.message ?? undefined
          };
        }

        return currentState;
      }
    }
  });

  // Make sure the conversationId is filled starting from the first interaction
  useEffect(() => {
    if (initialConversationId === '') {
      startConversation(chatSource);
    }
  }, [initialConversationId, chatSource, startConversation]);

  const mergedState: AdviceCueState = {
    ...state,
    uiMessages: [...history, ...state.uiMessages],
    followUpQuestions:
      isHydrated && followUpQuestions.length ? followUpQuestions : product.adviceCue.questions
  };

  const conversationId = streamConversationId || initialConversationId || '';

  const adjustScrollPosition = () => {
    boxRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollBottomToViewportMiddle = () => {
    if (!boxRef.current) return;

    const rect = boxRef.current?.getBoundingClientRect();
    const viewportHeight = window?.innerHeight;

    const delta = rect.bottom - viewportHeight / 2;

    window?.scrollBy({
      top: delta,
      behavior: 'smooth'
    });
  };

  return (
    <Box
      ref={boxRef}
      sx={{
        position: 'sticky',
        top: `calc(var(--cb-header-height) + ${freddyTheme.spacing[1000]})`,
        scrollMarginTop: freddyTheme.spacing[1000],
        [freddyTheme.breakpoint.minWidth('md')]: {
          scrollMarginTop: `calc(var(--cb-header-height) + ${freddyTheme.spacing[1000]})`
        }
      }}
    >
      <AdviceCue
        conversationId={conversationId}
        title={
          accountName
            ? t('adviceCues.title.withName', {
                customerName: accountName
              })
            : t('adviceCues.title.withoutName')
        }
        state={mergedState}
        onSendMessage={message => {
          sendUserMessage(message, {
            chatSource
          });
          adjustScrollPosition();
        }}
        productTypeName={productTypeName}
        resendFailedUserMessage={() =>
          resendFailedUserMessage({ chatSource, clientContext: { productId: id.toString() } })
        }
        onInputFocusChange={hasFocus => {
          if (isMobile && hasFocus) {
            scrollBottomToViewportMiddle();
          }
        }}
      />
      <FeedbackLink
        conversationId={conversationId}
        chatSource={chatSource}
        productId={id.toString()}
      />

      {isShownInternallyOnly && (
        <Typography variant='caption' mt={200}>
          This cue is currently available only for internal users.
        </Typography>
      )}
    </Box>
  );
};

export default AdviceCueWrapper;
